<template>
  <section class="customer-details" :aria-label="`${company.name} settings`">
    <header class="customer-details-header">
      <div>
        <p class="customer-eyebrow">Customer settings</p>
        <h2>
          {{ company.name }} <span class="customer-status">{{ company.isActive ? 'Active' : 'Archived' }}</span>
        </h2>
        <p class="customer-identity">Client № {{ company.clientNumber || '—' }}</p>
      </div>
    </header>
    <nav class="customer-sections" aria-label="Customer settings sections">
      <button
        v-for="section in sections"
        :key="section.id"
        type="button"
        :aria-current="activeSection === section.id ? 'page' : undefined"
        :class="{ selected: activeSection === section.id }"
        @click="selectSection(section.id)"
      >
        <mdicon :name="section.icon" size="18" aria-hidden="true" />
        <span>{{ section.label }}</span>
      </button>
    </nav>

    <div v-show="activeSection === 'overview'" class="customer-panel">
      <div class="customer-section-intro">
        <h3>Customer overview</h3>
        <p>Contact details, addresses and internal notes.</p>
      </div>
      <div class="customer-overview-grid">
        <section class="customer-card customer-contact">
          <CustomerContactEditor :company="company" />
        </section>
        <section class="customer-card customer-addresses">
          <header>
            <h4>Addresses</h4>
          </header>
          <div class="customer-address-grid">
            <section class="customer-address-section" aria-label="Invoice address">
              <h5>Invoice address</h5>
              <p class="customer-address-purpose">Used for billing.</p>
              <dl class="customer-address">
                <template
                  v-for="fieldName in addressFields.filter((field) => field !== 'clientNumber')"
                  :key="fieldName"
                >
                  <div
                    v-if="company.clientAddress?.[fieldName]"
                    :class="{
                      'customer-address-wide': ['companyName', 'addressLine', 'addressLine2'].includes(fieldName),
                    }"
                  >
                    <dt>{{ companyFieldNames.address[fieldName] }}</dt>
                    <dd>{{ company.clientAddress[fieldName] }}</dd>
                  </div>
                </template>
              </dl>
              <p v-if="!addressFields.some((field) => company.clientAddress?.[field])" class="customer-muted">
                No invoice address added.
              </p>
              <div class="customer-address-action">
                <b-button variant="outline-secondary" @click="openModal(EDIT_COMPANY_ADDRESS_MODAL, company)">
                  {{ company.clientAddress?.id ? 'Edit invoice address' : 'Add invoice address' }}
                </b-button>
              </div>
            </section>
            <section class="customer-address-section" aria-label="Sender addresses">
              <h5>Sender addresses</h5>
              <p class="customer-address-purpose">Default address for shipments. Manage all sender addresses below.</p>
              <dl class="customer-address">
                <template v-for="fieldName in addressFields" :key="fieldName">
                  <div
                    v-if="company.senderAddress?.[fieldName]"
                    :class="{
                      'customer-address-wide': ['companyName', 'addressLine', 'addressLine2'].includes(fieldName),
                    }"
                  >
                    <dt>{{ companyFieldNames.address[fieldName] }}</dt>
                    <dd>{{ company.senderAddress[fieldName] }}</dd>
                  </div>
                </template>
              </dl>
              <p v-if="!addressFields.some((field) => company.senderAddress?.[field])" class="customer-muted">
                No default sender address.
              </p>
              <div class="customer-address-action">
                <b-button variant="outline-secondary" @click="openSenderAddressesModal(company)">
                  Manage sender addresses
                </b-button>
              </div>
            </section>
          </div>
        </section>
        <section class="customer-card customer-notes">
          <header>
            <h4>Internal notes</h4>
            <b-button v-if="!isEditMode" variant="outline-secondary" @click="isEditMode = true">
              {{ note ? 'Edit note' : 'Add note' }}
            </b-button>
          </header>
          <p v-if="!isEditMode" class="customer-note" :class="{ 'customer-muted': !note }">
            {{ note || 'No internal notes for this customer.' }}
          </p>
          <template v-else>
            <label :for="`customer-note-${company.id}`" class="sr-only">Internal notes</label><textarea :id="`customer-note-${company.id}`" v-model="note" class="form-control" rows="4" />
            <div class="customer-card-actions">
              <b-button class="btn-grey" @click="cancelNote">Cancel</b-button><b-button class="btn-green" @click="saveNote">Save note</b-button>
            </div>
          </template>
        </section>
      </div>
    </div>

    <div v-show="activeSection === 'shipping'" class="customer-panel">
      <div class="customer-section-intro">
        <h3>Shipping</h3>
        <p>Control transport selection, processing and shipment notifications for this customer.</p>
      </div>
      <div class="customer-settings-list">
        <CustomerSettingRow
          title="Fallback carriers & weight limit"
          description="Choose fallback carriers and the product weight above which only fallback options are considered."
          :status="
            company.packageFallbackWeightLimit
              ? `Over ${company.packageFallbackWeightLimit.toLocaleString('en-US')} g: fallback only`
              : 'No customer weight limit'
          "
        >
          <FallbackCarriersModal :company="company" />
        </CustomerSettingRow>
        <CustomerSettingRow
          title="Buffering"
          description="Hold incoming shipments for processing based on cutoff time, item count or elapsed time."
          :status="company.isBufferingEnabled ? 'Enabled' : 'Disabled'"
        >
          <div class="customer-inline-controls">
            <BufferingModal v-if="company.isBufferingEnabled" :company="company" />
            <CustomerBooleanSetting :company="company" field="isBufferingEnabled" label="Buffering" />
          </div>
        </CustomerSettingRow>
        <CustomerSettingRow
          title="PostNord batch tier"
          description="Use batch tier optimization when selecting PostNord package services."
          :status="company.isBatchTierEnabled ? 'Enabled' : 'Disabled'"
        >
          <CustomerBooleanSetting :company="company" field="isBatchTierEnabled" label="PostNord batch tier" />
        </CustomerSettingRow>
        <CustomerSettingRow
          title="Email notifications"
          description="Control shipment email notifications for this customer."
          :status="company.shipmentNotifications ? 'Enabled' : 'Disabled'"
        >
          <CustomerBooleanSetting :company="company" field="shipmentNotifications" label="Email notifications" />
        </CustomerSettingRow>
        <CustomerSettingRow
          title="Automatic parcel pickup"
          description="Manage automatic pickup for parcel shipments."
          :status="company.autoPickupForParcelShipments ? 'Enabled' : 'Disabled'"
        >
          <AutoPickupInfoModal /><CustomerBooleanSetting
            :company="company"
            field="autoPickupForParcelShipments"
            label="Automatic parcel pickup"
          />
        </CustomerSettingRow>
      </div>
    </div>

    <div v-show="activeSection === 'pricing'" class="customer-panel">
      <div class="customer-section-intro">
        <h3>Pricing & billing</h3>
        <p>Customer prices, carrier cost margins and tax information.</p>
      </div>
      <div class="customer-settings-list">
        <CustomerSettingRow
          title="Dynamic pricing"
          description="Review customer-specific prices and their differences from the defaults."
        >
          <router-link
            class="btn btn-outline-secondary"
            :to="{ name: STAFF_CUSTOMERS_PRICES, params: { id: company.id } }"
          >
            Manage prices
          </router-link>
          <template #details>
            <DynamicPricingStatus :has-custom-prices="company.hasCustomPrices" /><button
              v-if="company.hasCustomPrices"
              type="button"
              class="customer-text-button"
              @click="showDiff"
            >
              {{ isDiffShown ? 'Hide comparison' : 'Compare with standard prices' }}
            </button><button type="button" class="customer-text-button ml-3" @click="showPriceHistory">
              View price history
            </button><PriceDifferenceList v-if="company.hasCustomPrices && isDiffShown" :customer-prices="customerPrices" />
          </template>
        </CustomerSettingRow>
        <CustomerSettingRow title="Hide optimized prices" description="Hide optimized prices from this customer.">
          <CustomerBooleanSetting :company="company" field="hidePrice" label="Hide optimized prices" />
        </CustomerSettingRow>
        <CustomerSettingRow
          title="Sender address prices"
          description="Manage prices associated with the customer's sender addresses."
        >
          <router-link
            class="btn btn-outline-secondary"
            :to="{ name: STAFF_SENDER_ADDRESS_PRICES, params: { id: company.id } }"
          >
            Manage address prices
          </router-link>
        </CustomerSettingRow>
        <CustomerSettingRow
          title="Cost margins"
          description="Set the percentage added to each carrier's cost for new price calculations."
        >
          <CostMarginsModal :company="company" />
        </CustomerSettingRow>
        <CustomerSettingRow title="Tax numbers" description="Manage the customer's tax identifiers.">
          <b-button variant="outline-secondary" @click="openTaxNumbersModal(company)"> Manage </b-button>
        </CustomerSettingRow>
      </div>
    </div>

    <div v-if="activeSection === 'activity'" class="customer-panel">
      <CustomerActivity :company-id="company.id" :initial-category="activityCategory" />
    </div>

    <div v-if="activeSection === 'users'" class="customer-panel">
      <CustomerUsers :company-id="company.id" />
    </div>

    <div v-show="activeSection === 'access'" class="customer-panel">
      <div class="customer-section-intro">
        <h3>Integrations & features</h3>
        <p>Manage connected systems and the features available to this customer.</p>
      </div>
      <div class="customer-settings-list">
        <CustomerSettingRow title="API keys" description="Manage credentials used to access the API for this customer.">
          <ApiKeysModal :company="company" />
        </CustomerSettingRow>
        <CustomerSettingRow title="External ID" description="Identifier used by connected systems.">
          <span>{{ company.externalId || 'Not set' }}</span>
        </CustomerSettingRow>
        <CustomerSettingRow
          title="Integrations"
          description="Manage connections between this customer and external systems."
        >
          <b-button variant="outline-secondary" @click="openIntegrationsModal(company)"> Settings </b-button>
        </CustomerSettingRow>
        <CustomerSettingRow title="Internal scans" description="Show internal scans to all users for this customer.">
          <CustomerBooleanSetting :company="company" field="shouldDisplayScans" label="Show internal scans" />
        </CustomerSettingRow>
        <CustomerSettingRow
          title="Inventory management"
          description="Allow this customer to use inventory management."
          :status="company.isInventoryManagementAvailable ? 'Available' : 'Not available'"
        >
          <InventoryManagementInfoModal /><CustomerBooleanSetting
            :company="company"
            field="isInventoryManagementAvailable"
            label="Inventory management"
          />
        </CustomerSettingRow>
        <CustomerSettingRow
          title="Address book"
          description="Allow this customer to use the address book."
          :status="company.isAddressBookAvailable ? 'Available' : 'Not available'"
        >
          <CustomerBooleanSetting :company="company" field="isAddressBookAvailable" label="Address book" />
        </CustomerSettingRow>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import type { KeyAccessor } from '@/types/Helpers/Arrays'
import type { PriceDifference } from '@/types/Models/BasePrice'
import type { Company } from '@/types/Models/Company'
import type { CustomerActivityCategory } from '@/types/Models/CustomerActivity'
import type { IntegrationsModal } from '@/types/Models/Integration'
import type { UsersModal } from '@/types/Models/User'
import { BButton } from 'bootstrap-vue-next'
import {
  defineComponent,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  watch,
  computed,
  type ComputedRef,
  type PropType,
  ref,
  type Ref,
} from 'vue'
import {
  EDIT_COMPANY_ADDRESS_MODAL,
  INTEGRATIONS,
  SENDER_ADDRESSES,
  SENDER_ADDRESS_ACTION,
  USERS,
  EDIT_COMPANY_TAX_NUMBERS_MODAL,
} from '@/const/ModalTypes'
import { none, ucFirst } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { useCustomersStore } from '@/store/customers'
import { useCustomersIntegrationsStore } from '@/store/customers/integrations'
import { useCustomersSenderAddressesStore } from '@/store/customers/senderAddresses'
import { usePricesStore } from '@/store/prices'
import { STAFF_CUSTOMERS_PRICES, STAFF_SENDER_ADDRESS_PRICES } from '@/types/router'
import ApiKeysModal from '@/views/Modals/Customer/ApiKeysModal.vue'
import CompanyProxy from '@/proxies/CompanyProxy'
import CustomerUsers from '@/views/Components/Customer/CustomerUsers.vue'
import BufferingModal from '@/views/Modals/Customer/BufferingModal.vue'
import CostMarginsModal from '@/views/Modals/Customer/CostMarginsModal.vue'
import CustomerActivity from '@/views/Components/Customer/CustomerActivity.vue'
import CustomerSettingRow from '@/views/Components/Customer/CustomerSettingRow.vue'
import AutoPickupInfoModal from '@/views/Components/Customer/AutoPickupInfoModal.vue'
import PriceDifferenceList from '@/views/Components/Customer/PriceDifference.vue'
import DynamicPricingStatus from '@/views/Components/Customer/DynamicPricingStatus.vue'
import CustomerContactEditor from '@/views/Components/Customer/CustomerContactEditor.vue'
import FallbackCarriersModal from '@/views/Modals/Customer/FallbackCarriersModal.vue'
import CustomerBooleanSetting from '@/views/Components/Customer/CustomerBooleanSetting.vue'
import InventoryManagementInfoModal from '@/views/Components/Customer/InventoryManagementInfoModal.vue'

export default defineComponent({
  name: 'ChildRowCompanyData',
  components: {
    CustomerSettingRow,
    CustomerContactEditor,
    CustomerUsers,
    CustomerActivity,
    CustomerBooleanSetting,
    BButton,
    BufferingModal,
    DynamicPricingStatus,
    PriceDifferenceList,
    ApiKeysModal,
    AutoPickupInfoModal,
    InventoryManagementInfoModal,
    FallbackCarriersModal,
    CostMarginsModal,
  },
  props: {
    company: {
      type: Object as PropType<Company>,
      required: true,
    },
  },
  setup(props) {
    const customersStore = useCustomersStore()
    const customersSenderAddressesStore = useCustomersSenderAddressesStore()
    const customersIntegrationsStore = useCustomersIntegrationsStore()

    const note: Ref<string | null> = ref(props.company.note)
    const isEditMode: Ref<boolean> = ref(false)
    const activeSection: Ref<string> = ref('overview')
    const activityCategory: Ref<CustomerActivityCategory> = ref('all')
    function selectSection(sectionId: string): void {
      activityCategory.value = 'all'
      activeSection.value = sectionId
    }
    function showPriceHistory(): void {
      activityCategory.value = 'prices'
      activeSection.value = 'activity'
    }
    const sections = [
      { id: 'overview', icon: 'view-dashboard-outline', label: 'Overview' },
      { id: 'shipping', icon: 'truck-outline', label: 'Shipping' },
      { id: 'pricing', icon: 'receipt-text-outline', label: 'Pricing & billing' },
      { id: 'access', icon: 'puzzle-outline', label: 'Integrations & features' },
      { id: 'users', icon: 'account-group-outline', label: 'Users' },
      { id: 'activity', icon: 'history', label: 'Activity' },
    ]
    const isDiffShown: Ref<boolean> = ref(false)
    const addressFields = [
      'companyName',
      'addressLine',
      'addressLine2',
      'postalCode',
      'city',
      'countryCode',
      'clientNumber',
    ]
    const generalInfoFields = [
      'email',
      'clientNumber',
      'contactName',
      'contactLastName',
      'phoneNumber',
      'contractParty',
    ]
    const companyFieldNames: KeyAccessor = {
      email: 'Email address',
      clientNumber: 'Client №',
      contactName: 'First name',
      contactLastName: 'Last name',
      address: {
        companyName: 'Company',
        countryCode: 'Country',
        city: 'City',
        postalCode: 'Postcode',
        addressLine: 'Address',
        addressLine2: 'Address line 2',
        clientNumber: 'Client №',
      },
      phoneNumber: 'Phone number',
      autoPickupForParcelShipments: 'Automatic parcel pickup',
      isInventoryManagementAvailable: 'Inventory management',
      isAddressBookAvailable: 'Address book',
      contractParty: 'Contract party',
      salesman: 'Assigned Salesman',
    }

    const getSalesmanInfo = (salesman: any): string => {
      if (salesman && Object.keys(salesman).length > 0) {
        const fullName = `${salesman.first_name} ${salesman.last_name}`
        return fullName.length > 2 ? fullName : salesman.email
      }
      return ''
    }

    const openSenderAddressesModal = (data: Company): void => {
      customersSenderAddressesStore
        .fetch(data.id)
        .then((response) => {
          if (!response) return
          if (!response.data.length) {
            openModal(SENDER_ADDRESS_ACTION, { customerId: data.id })
            return
          }
          openModal(SENDER_ADDRESSES, data)
        })
        .catch(none)
    }
    const openUsersList = (id: string): void => {
      openModal(USERS, { companyId: id } as UsersModal)
    }
    const openIntegrationsModal = (data: Company): void => {
      customersIntegrationsStore.reset()
      customersIntegrationsStore.fetch(data.id).then(() => {
        openModal(INTEGRATIONS, { id: data.id, company: data } as IntegrationsModal)
      })
    }
    const openTaxNumbersModal = (data: Company): void => {
      // open the tax numbers modal and pass company id as modal props
      openModal(EDIT_COMPANY_TAX_NUMBERS_MODAL, { id: data.id, company: data })
    }
    const saveNote = (): void => {
      customersStore
        .update({
          id: props.company.id,
          data: { note: note.value },
        })
        .then(() => {
          isEditMode.value = false
        })
        .catch(none)
    }
    const cancelNote = (): void => {
      note.value = props.company.note
      isEditMode.value = false
    }

    const showDiff = (e: Event): boolean => {
      e.preventDefault()
      isDiffShown.value = !isDiffShown.value
      return false
    }

    const pricesStore = usePricesStore()

    const customerPrices: ComputedRef<PriceDifference[]> = computed(
      () => pricesStore.differencePrices[props.company.id] || [],
    )

    onBeforeMount(() => {
      if (props.company.hasCustomPrices) {
        pricesStore.fetchDifferencePrices(props.company.id)
      }
    })

    let refreshingPricing = false
    const refreshPricingStatus = (): void => {
      if (activeSection.value !== 'pricing' || refreshingPricing) return
      refreshingPricing = true
      new CompanyProxy()
        .find(props.company.id)
        .then((result) => {
          const customer = customersStore.listCustomers?.find((item) => item.id === props.company.id)
          if (customer && typeof result.data?.hasCustomPrices === 'boolean') {
            customer.hasCustomPrices = result.data.hasCustomPrices
            if (customer.hasCustomPrices) return pricesStore.fetchDifferencePrices(customer.id)
            pricesStore.differencePrices[customer.id] = []
          }
        })
        .catch(none)
        .finally(() => {
          refreshingPricing = false
        })
    }
    watch(activeSection, refreshPricingStatus)
    onMounted(() => window.addEventListener('focus', refreshPricingStatus))
    onBeforeUnmount(() => window.removeEventListener('focus', refreshPricingStatus))

    return {
      activeSection,
      activityCategory,
      selectSection,
      showPriceHistory,
      sections,
      note,
      isEditMode,
      isDiffShown,
      addressFields,
      customerPrices,
      companyFieldNames,
      generalInfoFields,
      ucFirst,
      saveNote,
      showDiff,
      openModal,
      cancelNote,
      openUsersList,
      getSalesmanInfo,
      openTaxNumbersModal,
      openIntegrationsModal,
      openSenderAddressesModal,

      STAFF_CUSTOMERS_PRICES,
      EDIT_COMPANY_ADDRESS_MODAL,
      STAFF_SENDER_ADDRESS_PRICES,
      EDIT_COMPANY_TAX_NUMBERS_MODAL,
    }
  },
})
</script>
